import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';

const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to   = ((endEpoch   || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

const TIME_OPTIONS = [
  { label: '5m',  minutes: 5 },
  { label: '10m', minutes: 10 },
  { label: '30m', minutes: 30 },
  { label: '1h',  minutes: 60 },
  { label: '2h',  minutes: 120 },
  { label: '6h',  minutes: 360 },
  { label: '12h', minutes: 720 },
  { label: '24h', minutes: 1440 },
];

function getMOSColor(mos) {
  if (!mos) return null;
  if (mos >= 4.0) return 'success';
  if (mos >= 3.5) return 'info';
  if (mos >= 3.0) return 'warning';
  return 'error';
}

function getPacketLossColor(loss) {
  if (loss === null || loss === undefined) return null;
  if (loss < 1)  return 'success';
  if (loss < 3)  return 'warning';
  if (loss < 5)  return 'orange';
  return 'error';
}

function MOSChip({ value }) {
  if (!value) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Chip
      label={value.toFixed(2)}
      size="small"
      color={getMOSColor(value)}
      sx={{ minWidth: 52 }}
    />
  );
}

function LossChip({ value }) {
  if (value === null || value === undefined)
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  const color = getPacketLossColor(value);
  if (color === 'orange') {
    return (
      <Chip
        label={`${value.toFixed(2)}%`}
        size="small"
        sx={{ backgroundColor: '#ed6c02', color: '#fff', minWidth: 60 }}
      />
    );
  }
  return (
    <Chip
      label={`${value.toFixed(2)}%`}
      size="small"
      color={color || 'default'}
      sx={{ minWidth: 60 }}
    />
  );
}

const DIVIDER_SX = { borderLeft: '1px solid rgba(255,255,255,0.08)' };

function LegStats({ data, isFirst }) {
  const firstCellSx = isFirst ? DIVIDER_SX : {};
  if (!data) {
    return (
      <>
        <TableCell align="center" sx={firstCellSx}>
          <Typography variant="body2" color="text.secondary">—</Typography>
        </TableCell>
        <TableCell align="center"><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
        <TableCell align="center"><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
        <TableCell align="center"><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
        <TableCell align="center"><Typography variant="body2" color="text.secondary">—</Typography></TableCell>
      </>
    );
  }
  return (
    <>
      <TableCell align="center" sx={firstCellSx}><MOSChip value={data.avg_mos} /></TableCell>
      <TableCell align="center"><LossChip value={data.avg_packet_loss} /></TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {data.avg_jitter != null ? `${data.avg_jitter.toFixed(0)}ms` : '—'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">{data.total_calls}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {data.asr != null ? `${data.asr.toFixed(1)}%` : '—'}
        </Typography>
      </TableCell>
    </>
  );
}

function extractAcdCallId(to) {
  if (!to) return null;
  const m = to.match(/^\*\*(\d+)\*/);
  return m ? m[1] : null;
}

function WorstCallsDialog({ switchname, cluster, minutes, open, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !switchname) return;
    setLoading(true);
    setError(null);
    setCalls([]);
    monitoringAPI.getMediaServerWorstCalls(switchname, minutes)
      .then(res => setCalls(res.data.calls || []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load calls'))
      .finally(() => setLoading(false));
  }, [open, switchname, minutes]);

  const handleExport = () => {
    if (!calls.length) return;
    const headers = [
      'datetime', 'sip_call_id', 'sip_from_user', 'sip_to_user',
      'direction', 'duration', 'mos', 'packet_loss', 'jitter', 'quality_pct', 'hangup_cause',
    ];
    const rows = calls.map(c => [
      c.datetime || '',
      c.sip_call_id || '',
      c.sip_from_user || '',
      c.sip_to_user || '',
      c.direction || '',
      c.duration != null ? c.duration : '',
      c.mos != null ? c.mos : '',
      c.packet_loss != null ? c.packet_loss : '',
      c.jitter != null ? c.jitter : '',
      c.quality_pct != null ? c.quality_pct : '',
      c.hangup_cause || '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `worst-calls-${switchname}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Top 50 Worst Calls — {switchname}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {!loading && !error && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell align="right">Dur (s)</TableCell>
                <TableCell align="center">MOS</TableCell>
                <TableCell align="center">Loss%</TableCell>
                <TableCell align="right">Jitter</TableCell>
                <TableCell align="right">Quality%</TableCell>
                <TableCell>Cause</TableCell>
                <TableCell>SIP Call-ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {call.datetime
                      ? new Date(call.datetime + 'Z').toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell>{call.sip_from_user || '—'}</TableCell>
                  <TableCell>
                    {(() => {
                      const acdId = extractAcdCallId(call.sip_to_user);
                      if (acdId && cluster) {
                        return (
                          <a
                            href={`https://${cluster}.singlecomm.com/supervisor/call.sc?id=${acdId}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ color: '#90caf9', textDecoration: 'none', fontSize: '0.82rem' }}
                          >
                            {call.sip_to_user}
                          </a>
                        );
                      }
                      return call.sip_to_user || '—';
                    })()}
                  </TableCell>
                  <TableCell>{call.direction || '—'}</TableCell>
                  <TableCell align="right">{call.duration ?? '—'}</TableCell>
                  <TableCell align="center"><MOSChip value={call.mos} /></TableCell>
                  <TableCell align="center"><LossChip value={call.packet_loss} /></TableCell>
                  <TableCell align="right">
                    {call.jitter != null ? `${call.jitter.toFixed(0)}ms` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {call.quality_pct != null ? `${call.quality_pct.toFixed(1)}%` : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={call.hangup_cause || '?'} size="small" />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const homerUrl = buildHomerUrl(call.sip_call_id, call.start_epoch, call.end_epoch);
                      return homerUrl ? (
                        <Tooltip title={call.sip_call_id || ''} arrow>
                          <a
                            href={homerUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              color: '#90caf9',
                              textDecoration: 'none',
                              display: 'block',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.82rem',
                            }}
                          >
                            {call.sip_call_id}
                          </a>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
              {!calls.length && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No calls found for this time window
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={!calls.length}
          variant="outlined"
          size="small"
        >
          Export CSV ({calls.length} calls)
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MediaServersPage() {
  const [minutes, setMinutes] = useState(60);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    monitoringAPI.getMediaServersQoS(minutes)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load data'))
      .finally(() => setLoading(false));
  }, [minutes]);

  useEffect(() => { load(); }, [load]);

  const colHeaders = ['MOS', 'Loss%', 'Jitter', 'Calls', 'ASR'];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Media Servers QoS
        </Typography>
        <ToggleButtonGroup
          value={minutes}
          exclusive
          onChange={(_, v) => { if (v !== null) setMinutes(v); }}
          size="small"
        >
          {TIME_OPTIONS.map(o => (
            <ToggleButton key={o.minutes} value={o.minutes}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {data?.timestamp && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Last updated: {new Date(data.timestamp + 'Z').toLocaleString()}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && data?.clusters?.map(cluster => (
        <Card key={cluster.cluster} sx={{ mb: 3 }} component={Paper} elevation={2}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <StorageIcon color="primary" fontSize="small" />
              <Typography variant="h6">
                Cluster: <strong>{cluster.cluster}</strong>
              </Typography>
              <Chip
                label={`${cluster.media_nodes.length} node${cluster.media_nodes.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  {/* Group header row */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: 'bold',
                        verticalAlign: 'bottom',
                        borderRight: '1px solid rgba(255,255,255,0.08)',
                        minWidth: 220,
                      }}
                    >
                      Media Node
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: 'primary.light', fontWeight: 'bold', borderBottom: 0 }}
                    >
                      Agent
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: 'success.light', fontWeight: 'bold', borderBottom: 0, ...DIVIDER_SX }}
                    >
                      Inbound
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: 'warning.light', fontWeight: 'bold', borderBottom: 0, ...DIVIDER_SX }}
                    >
                      Outbound
                    </TableCell>
                  </TableRow>
                  {/* Sub-header row */}
                  <TableRow>
                    {colHeaders.map(col => (
                      <TableCell key={`a-${col}`} align="center" sx={{ fontSize: '0.72rem' }}>
                        {col}
                      </TableCell>
                    ))}
                    {colHeaders.map((col, i) => (
                      <TableCell
                        key={`in-${col}`}
                        align="center"
                        sx={{ fontSize: '0.72rem', ...(i === 0 ? DIVIDER_SX : {}) }}
                      >
                        {col}
                      </TableCell>
                    ))}
                    {colHeaders.map((col, i) => (
                      <TableCell
                        key={`out-${col}`}
                        align="center"
                        sx={{ fontSize: '0.72rem', ...(i === 0 ? DIVIDER_SX : {}) }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {cluster.media_nodes.map(node => (
                    <Tooltip
                      key={node.switchname}
                      title="Click to view top 50 worst calls"
                      placement="left"
                      arrow
                    >
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedNode({ switchname: node.switchname, cluster: cluster.cluster })}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            borderRight: '1px solid rgba(255,255,255,0.08)',
                            fontFamily: 'monospace',
                            fontSize: '0.82rem',
                          }}
                        >
                          {node.switchname}
                        </TableCell>
                        <LegStats data={node.agent} />
                        <LegStats data={node.inbound} isFirst />
                        <LegStats data={node.outbound} isFirst />
                      </TableRow>
                    </Tooltip>
                  ))}

                  {cluster.media_nodes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={16} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No media nodes found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      ))}

      {!loading && !error && data?.clusters?.length === 0 && (
        <Alert severity="info">
          No data available for the selected time window.
        </Alert>
      )}

      <WorstCallsDialog
        open={!!selectedNode}
        switchname={selectedNode?.switchname}
        cluster={selectedNode?.cluster}
        minutes={minutes}
        onClose={() => setSelectedNode(null)}
      />
    </Box>
  );
}
